export const UNKNOWN = 'unknown';
import lookupLocation from '../../static/locationLookup.json';
export const fetchData = async (endpoint, timeout = 15000) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const response = await fetch(endpoint, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Error in Network: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (err) {
    if (err.name === 'AbortError') {
      console.warn(`Request timed out after ${timeout}ms for endpoint:`, endpoint);
    } else {
      console.warn('Error while getting data', err);
    }
    return null;
  }
};

export const fetchCollectionMetadata = async (collectionUrl) => {
  try {
    const metaData = await fetchData(collectionUrl);
    return metaData;
  } catch (err) {
    console.error('Error fetching data: ', err);
  }
};

export const getCoverageData = async (url, timeout = 15000) => {
  const coverageData = await fetchData(url, timeout);
  return coverageData;
};

export async function getLocationForFeature(feature) {
  const { properties } = feature;
  const id  = properties['Plume ID'];
  const lat = properties['Latitude of max concentration'];
  const lon = properties['Longitude of max concentration'];

  const locationFromLookup = lookupLocation[id];
  if (typeof locationFromLookup === 'string' && locationFromLookup !== UNKNOWN) {
    return locationFromLookup;
  }

  const apiKey = config.geoApifyKey ?? process.env.REACT_APP_GEOAPIFY_APIKEY;
  if (!apiKey) {
    console.warn('No Geoapify API key for location lookup. Returning empty string.');
    return '';
  }

  const baseEndpoint =
    config.latlonEndpoint ??
    process.env.REACT_APP_LAT_LON_TO_COUNTRY_ENDPOINT;
  const endpoint = `${baseEndpoint}?lat=${lat}&lon=${lon}&apiKey=${apiKey}`;

  try {
    const result = await fetchLocationFromEndpoint(lat, lon, endpoint);
    return result;
  } catch (err) {
    console.error('❌ Error fetching location from endpoint:', err);
    return '';
  }
}


export function getAllLocation() {
  return lookupLocation;
}

export const fetchLocationFromEndpoint = async (lat, lon, endpoint) => {
  let location = '';
  try {
    const location_data = await fetchData(endpoint);
    let location_properties = location_data.features[0].properties;
    let sub_location =
      location_properties['city'] || location_properties['county'] || UNKNOWN;
    let state = location_properties['state']
      ? `${location_properties['state']}, `
      : '';
    let country = location_properties['country']
      ? location_properties['country']
      : '';
    location = `${sub_location}, ${state} ${country}`;
  } catch (error) {
    console.warn(`Error fetching location for ${lat}, ${lon}:`, error);

    location = UNKNOWN;
  }
  return location;
};

export const fetchAllFromSTACAPI = async (STACApiUrl) => {
  // it will fetch all collection items from all stac api.
  // do not provide offset and limits in the url
  try {
    // fetch in the collection from the stac api
    const jsonResult = await fetchData(STACApiUrl);
    if (!jsonResult) return [];

    // need to pull in remaining data based on the pagination information
    // NOTE: newer stac-fastapi reports `numberMatched`/`numberReturned`; older
    // deployments report the deprecated `context` extension instead.
    const matched = jsonResult.numberMatched ?? jsonResult.context?.matched;
    const returned = jsonResult.numberReturned ?? jsonResult.context?.returned;
    // if there are more data remaining fetch all
    if (matched > returned) {
      let allData = await fetchAllDataSTAC(STACApiUrl, matched);
      return allData;
    }
    // everything came back in this single response
    return getResultArray(jsonResult);
  } catch (error) {
    console.error('Error fetching data:', error);
    return [];
  }
};

// Requesting every item in a single page (limit=numberMatched) makes the STAC API
// respond 500, so page through the results at a size it serves reliably.
const STAC_PAGE_SIZE = 500;
// Backstop so a malformed `next` link can never spin forever.
const STAC_MAX_PAGES = 100;

const fetchAllDataSTAC = async (STACApiUrl, numberMatched) => {
  // NOTE: STAC API doesnot accept offset as a query params. So, follow the `next` links.
  try {
    const allData = [];
    let url = addOffsetsToURL(STACApiUrl, STAC_PAGE_SIZE);

    for (let page = 0; url && page < STAC_MAX_PAGES; page += 1) {
      const jsonResult = await fetchData(url);
      if (!jsonResult) break;
      const pageItems = getResultArray(jsonResult);
      if (!pageItems.length) break;
      allData.push(...pageItems);
      url = jsonResult.links?.find((link) => link.rel === 'next')?.href;
    }

    if (allData.length !== numberMatched) {
      console.warn(
        `STAC pagination incomplete: got ${allData.length} of ${numberMatched} items from ${STACApiUrl}`
      );
    }
    return allData;
  } catch (error) {
    console.error('Error fetching data:', error);
    return [];
  }
};

// helpers
const addOffsetsToURL = (url, limit) => {
  if (url.includes('?')) {
    return `${url}&limit=${limit}`;
  } else {
    return `${url}?limit=${limit}`;
  }
};

export const getResultArray = (result) => {
  if ('features' in result) {
    // the result is for collection item
    return result.features;
  }
  if ('collections' in result) {
    // the result is for collection
    return result.collections;
  }
  return [];
};
